import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Image, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import './Contacts.css';
import { showContacts, updateContact } from '../../../helpers';
import { Eye, PencilSquare, Trash } from 'react-bootstrap-icons';

interface Contact {
    id: string;
    name: string;
    mobile_no: string;
    group_name: string;
    profileUrl: string;
}

const Contact: React.FC = () => {
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [showViewModal, setShowViewModal] = useState<boolean>(false);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [editedContact, setEditedContact] = useState<Contact | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
    const [groups, setGroups] = useState<string[]>([]);
    const [activeGroup, setActiveGroup] = useState<string>('All');
    const [groupCounts, setGroupCounts] = useState<{ [key: string]: number }>({});

    useEffect(() => {
        const fetchContacts = async () => {
            try {
                const response = await showContacts();
                const normalizedContacts: Contact[] = response.data.map((contact: any) => ({
                    id: contact.contact_id,
                    name: contact.name || contact.contact_name,
                    mobile_no: contact.mobile_no || contact.mobile_no,
                    group_name: contact.group_name || contact.group_name,
                    profileUrl: contact.profileUrl || `https://mercury.skynyxserver.com/git_projects/WA-skynyx/file_storage/contacts/${contact.profile}`,
                }));

                setContacts(normalizedContacts);
                setFilteredContacts(normalizedContacts);

                const groupList = Array.from(
                    new Set(normalizedContacts.map(c => c.group_name))
                );
                setGroups(groupList);
                const counts: { [key: string]: number } = {};

                normalizedContacts.forEach(c => {
                    if (!counts[c.group_name]) counts[c.group_name] = 0;
                    counts[c.group_name]++;
                });
                counts["All"] = normalizedContacts.length;
                setGroupCounts(counts);
                setLoading(false);
            } catch (err) {
                setError('Failed to fetch contacts.');
                setLoading(false);
                console.error('Error fetching contacts:', err);
            }
        };

        fetchContacts();
    }, []);

    const filterByGroup = (group: string) => {
        setActiveGroup(group);

        if (group === "All") {
            setFilteredContacts(contacts);
        } else {
            setFilteredContacts(
                contacts.filter(contact => contact.group_name === group)
            );
        }
    };

    const handleView = (contact: Contact) => {
        setSelectedContact(contact);
        setShowViewModal(true);
    };

    const handleEdit = (contact: Contact) => {

        setEditedContact({ ...contact });
        setPreviewUrl(null);
        setSelectedFile(null);
        setShowEditModal(true);
    };

    const handleSaveEdit = async () => {
        if (!editedContact) return;

        const ContactPayload = {
            contact_name: editedContact.name,
            mobile_no: editedContact.mobile_no,
            group_name: editedContact.group_name,
            profile: previewUrl || editedContact.profileUrl // sending image as base64
        };

        try {
            await updateContact(editedContact.id, ContactPayload);

            setContacts(prev =>
                prev.map(c => (c.id === editedContact.id ? { ...c, ...ContactPayload } : c))
            );

            setShowEditModal(false);

        } catch (error) {
            console.error("Update failed", error);
            alert("Failed to update contact");
        }
    };



    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (editedContact) {
            setEditedContact({
                ...editedContact,
                [e.target.name]: e.target.value
            });
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            setSelectedFile(file);

            // Create preview URL
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <>
            <Card>
                <Card.Header className="bg-secondary d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 text-white">Contacts</h5>
                </Card.Header>
                <Card.Body>
                    <div className="mb-4 text-center">

                        <Button
                            variant={activeGroup === 'All' ? 'primary' : 'outline-primary'}
                            size="sm"
                            className="me-2 mb-2"
                            onClick={() => filterByGroup('All')}
                        >
                            All ({groupCounts["All"] || 0})
                        </Button>

                        {/* Dynamic Group Buttons */}
                        {groups.map((grp) => (
                            <Button
                                key={grp}
                                variant={activeGroup === grp ? 'primary' : 'outline-primary'}
                                size="sm"
                                className="me-2 mb-2"
                                onClick={() => filterByGroup(grp)}
                            >
                                {grp} ({groupCounts[grp] || 0})
                            </Button>
                        ))}

                    </div>

                    {loading && (
                        <Row>
                            <Col className="text-center py-4">
                                <Spinner animation="border" variant="primary" />
                            </Col>
                        </Row>
                    )}

                    {error && (
                        <Row>
                            <Col>
                                <Alert variant="danger">{error}</Alert>
                            </Col>
                        </Row>
                    )}

                    <Row>
                        {filteredContacts.map((contact) => (
                            <Col md={4} sm={6} xs={12} key={contact.id} className="mb-4">
                                <Card className="text-center shadow-sm h-100 contact-card" >
                                    <Card.Body>

                                        <Image
                                            src={contact.profileUrl}
                                            roundedCircle
                                            width={90}
                                            height={90}
                                            className="mb-3 contact-avatar"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/90'; // fallback image
                                            }}
                                        />
                                        <Card.Title className="contact-name">{contact.name}</Card.Title>
                                        <Card.Text className="contact-mobile_no">{contact.mobile_no}</Card.Text>
                                        <div className="d-flex justify-content-center gap-2">
                                            <Button
                                                variant="info"
                                                size="sm"
                                                onClick={() => handleView(contact)}
                                            >
                                               <Eye />
                                            </Button>
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleEdit(contact)}
                                            >
                                               <PencilSquare /> 
                                            </Button>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                            >
                                              <Trash />
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                </Card.Body>
            </Card>

            {/* View Contact Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Contact Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedContact && (
                        <div className="text-center">
                            <Image
                                src={selectedContact.profileUrl}
                                roundedCircle
                                width={120}
                                height={120}
                                className="mb-3"
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/120'; // fallback image
                                }}
                            />
                            <h4>{selectedContact.name}</h4>
                            <p className="text-muted">{selectedContact.mobile_no}</p>
                            <p className="text-muted">Group: {selectedContact.group_name}</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowViewModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Contact Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Contact</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editedContact && (
                        <Form>
                            <Form.Group className="mb-3 text-center">
                                <Image
                                    src={previewUrl || editedContact.profileUrl}
                                    roundedCircle
                                    width={120}
                                    height={120}
                                    className="mb-3"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/120'; // fallback image
                                    }}
                                />
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    className="mb-3"
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={editedContact.name}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Mobile Number</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="mobile_no"
                                    value={editedContact.mobile_no}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Group Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="group_name"
                                    value={editedContact.group_name}
                                    onChange={handleInputChange}
                                />
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSaveEdit}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Contact;